import { useToast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { APP_SETTINGS_QUERY_KEY } from "@/modules/app-settings/hooks/useAppSettingsQuery";
import { AppSettingsService } from "@/services/appSettings";
import { AppSettingValue, ReadAppSetting } from "@/types/appSettings";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface AppSettingsList {
  items: ReadAppSetting[];
  count: number;
}

export const useUpdateAppSettingMutation = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutationWithErrorHandling({
    mutationFn: ({ code, value }: { code: string; value: AppSettingValue }) =>
      AppSettingsService.updateSetting(code, value),
    // Przełącznik zmienia się od razu; zapis idzie w tle (lambdy bywają
    // "zimne" i odpowiadają po kilku sekundach). Przy błędzie wracamy do
    // poprzedniego stanu.
    onMutate: async ({ code, value }) => {
      await queryClient.cancelQueries({ queryKey: APP_SETTINGS_QUERY_KEY });
      const previous = queryClient.getQueryData<AppSettingsList>(
        APP_SETTINGS_QUERY_KEY,
      );
      if (previous) {
        queryClient.setQueryData<AppSettingsList>(APP_SETTINGS_QUERY_KEY, {
          ...previous,
          items: previous.items.map((setting) =>
            setting.code === code ? { ...setting, value } : setting,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(APP_SETTINGS_QUERY_KEY, context.previous);
      }
      toast({
        title: t("appSettings.errors.update"),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({ title: t("appSettings.success.update") });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY }),
  });
};
